/* eslint-disable */
import { cloneElement } from 'react';
import { Box, Typography, useMediaQuery } from '@mui/material';
import { motion } from 'framer-motion';
import CountUp from 'react-countup';
import PropTypes from 'prop-types';
import { formatIndianCurrency, formatIndianCount, buildAriaLabel } from '../utils/formatters';

/**
 * MetricCard — Compact glassmorphic metric tile.
 * Designed to fit 8 cards per row on desktop (2 rows for 15 metrics).
 *
 * Props API preserved: label, value, isVolume, icon, animationDelay
 */
function MetricCard({ label, value, isVolume, icon, animationDelay = 0 }) {
    const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

    const formattedValue = isVolume ? formatIndianCurrency(value) : formatIndianCount(value);
    const ariaLabel = buildAriaLabel(label, formattedValue);

    const accentGradient = isVolume
        ? 'linear-gradient(135deg, #10B981 0%, #059669 100%)'
        : 'linear-gradient(135deg, #6366F1 0%, #4F46E5 100%)';

    const glowColor = isVolume ? 'rgba(16, 185, 129, 0.15)' : 'rgba(99, 102, 241, 0.15)';

    const iconBg = isVolume
        ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.12) 0%, rgba(5, 150, 105, 0.06) 100%)'
        : 'linear-gradient(135deg, rgba(99, 102, 241, 0.12) 0%, rgba(79, 70, 229, 0.06) 100%)';

    const iconColor = isVolume ? '#059669' : '#4F46E5';

    const motionVariants = {
        hidden: { opacity: 0, y: 10, scale: 0.98 },
        visible: {
            opacity: 1,
            y: 0,
            scale: 1,
            transition: {
                duration: 0.28,
                delay: animationDelay,
                ease: [0.22, 1, 0.36, 1]
            }
        }
    };

    const renderValue = () => {
        const valueStyle = {
            fontWeight: 700,
            letterSpacing: '-0.03em',
            color: '#0F172A',
            fontSize: { xs: '16px', sm: '18px', md: '20px' },
            lineHeight: 1.2,
            fontFeatureSettings: '"tnum" on, "lnum" on'
        };

        if (prefersReducedMotion) {
            return (
                <Typography variant="h5" component="p" sx={valueStyle}>
                    {formattedValue}
                </Typography>
            );
        }

        return (
            <Typography variant="h5" component="p" sx={valueStyle}>
                <CountUp
                    end={value}
                    duration={1.2}
                    separator=","
                    prefix={isVolume ? '₹' : ''}
                    decimals={isVolume ? 2 : 0}
                    formattingFn={(val) => (isVolume ? formatIndianCurrency(val) : formatIndianCount(val))}
                />
            </Typography>
        );
    };

    const cardContent = (
        <Box
            aria-label={ariaLabel}
            tabIndex={0}
            sx={{
                position: 'relative',
                overflow: 'hidden',
                p: { xs: 1.5, sm: 1.75, md: 2 },
                display: 'flex',
                flexDirection: 'column',
                gap: 0.75,
                minHeight: { xs: '90px', md: '100px' },
                borderRadius: '14px',
                background: 'rgba(255, 255, 255, 0.75)',
                backdropFilter: 'blur(16px) saturate(180%)',
                WebkitBackdropFilter: 'blur(16px) saturate(180%)',
                border: '1px solid rgba(255, 255, 255, 0.85)',
                boxShadow: '0 1px 2px rgba(15, 23, 42, 0.02), 0 4px 16px -4px rgba(15, 23, 42, 0.04)',
                transition: 'all 240ms cubic-bezier(0.22, 1, 0.36, 1)',
                cursor: 'default',
                // Left accent strip — thinner, shorter
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: '12px',
                    left: 0,
                    width: '3px',
                    height: '28px',
                    borderRadius: '0 3px 3px 0',
                    background: accentGradient
                },
                '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 4px 12px -2px rgba(15, 23, 42, 0.05), 0 8px 24px -4px ${glowColor}`,
                    border: '1px solid rgba(255, 255, 255, 1)',
                    '& .metric-icon-compact': {
                        transform: 'scale(1.06)'
                    }
                },
                '&:focus-visible': {
                    outline: '2px solid #4F46E5',
                    outlineOffset: '2px'
                }
            }}
        >
            {/* Top row: icon + type dot */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                {icon && (
                    <Box
                        className="metric-icon-compact"
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 30,
                            height: 30,
                            borderRadius: '9px',
                            background: iconBg,
                            border: '1px solid',
                            borderColor: isVolume ? 'rgba(16, 185, 129, 0.12)' : 'rgba(99, 102, 241, 0.12)',
                            color: iconColor,
                            transition: 'all 240ms ease',
                            flexShrink: 0
                        }}
                        aria-hidden="true"
                    >
                        {cloneElement(icon, { sx: { fontSize: '16px' } })}
                    </Box>
                )}
                {/* Compact type indicator dot */}
                <Box
                    sx={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        background: accentGradient,
                        flexShrink: 0
                    }}
                    title={isVolume ? 'Volume' : 'Count'}
                />
            </Box>

            {/* Value */}
            <Box sx={{ mt: 'auto' }}>{renderValue()}</Box>

            {/* Label — compact */}
            <Typography
                variant="caption"
                component="h3"
                sx={{
                    fontWeight: 500,
                    color: '#64748B',
                    fontSize: '10.5px',
                    letterSpacing: '0.02em',
                    lineHeight: 1.3,
                    userSelect: 'none',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                }}
            >
                {label}
            </Typography>
        </Box>
    );

    if (prefersReducedMotion) {
        return cardContent;
    }

    return (
        <motion.div initial="hidden" animate="visible" variants={motionVariants} style={{ height: '100%' }}>
            {cardContent}
        </motion.div>
    );
}

MetricCard.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.number.isRequired,
    isVolume: PropTypes.bool.isRequired,
    icon: PropTypes.node,
    animationDelay: PropTypes.number
};

export default MetricCard;
